import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Grid, message, Modal } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
import { type LabelsDataFragment } from '../../../api/fragments/LabelsData';
import {
    DeleteLabelsDocument,
    type DeleteLabelsMutation,
    type DeleteLabelsMutationVariables,
} from '../../../api/mutations/deleteLabels';
import {
    UpdateLabelsDocument,
    type UpdateLabelsMutation,
    type UpdateLabelsMutationVariables,
} from '../../../api/mutations/updateLabels';
import { type LabelsInput } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import Form from '../../../components/fields/Form';
import NoItemResult from '../../../components/results/NoItemResult';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import LabelForm from './LabelForm';
import useLabelsValidator from './useValidators';

export type LabelFormikValue = LabelsInput & { moduleId?: string; moduleIds?: string[] };

type LabelsDetailsInnerPageProps = {
    label: LabelsDataFragment;
};

const LabelsDetailsInnerPage = ({ label }: LabelsDetailsInnerPageProps) => {
    const { t } = useTranslation('labelDetails');
    const navigate = useNavigate();
    const company = useCompany(true);
    const apolloClient = useApolloClient();
    const screens = Grid.useBreakpoint();

    const validate = useLabelsValidator();

    const initialValues: LabelFormikValue = useMemo(
        () => ({
            ...pick(['backgroundColor', 'labelName', 'textColor', 'isActive'], label),
            moduleVariants: label.moduleVariants.map(
                moduleVariant => ({
                    moduleId: moduleVariant.moduleId,
                    addedVariantSuiteIds: moduleVariant.addedVariantSuiteIds,
                }),
                []
            ),
            moduleIds: label.moduleVariants.map(moduleVariant => moduleVariant.moduleId) || [],
        }),
        [label]
    );

    const onSubmit = useHandleError<LabelFormikValue>(
        async value => {
            // submitting message
            message.loading({
                content: t('labelDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const { moduleId, ...valueSetting } = value;
            const settings = {
                ...pick(['backgroundColor', 'textColor', 'labelName', 'moduleVariants', 'isActive'], valueSetting),
            };

            await apolloClient.mutate<UpdateLabelsMutation, UpdateLabelsMutationVariables>({
                mutation: UpdateLabelsDocument,
                variables: {
                    id: label.id,
                    settings,
                },
            });

            // inform about success
            message.success({
                content: t('labelDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            navigate('/admin/labels');
        },
        [apolloClient, label.id, navigate, t]
    );

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: label.versioning,
        moduleId: label.labelModule.id,
    });

    const onDeleteLabel = useCallback(async () => {
        Modal.confirm({
            title: t('labelDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('labelDetails:deleteModal.content'),
            okText: t('labelDetails:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('labelDetails:deleteModal.cancelText'),
            async onOk() {
                try {
                    // loading message
                    message.loading({
                        content: t('labelDetails:messages.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with the API
                    await apolloClient.mutate<DeleteLabelsMutation, DeleteLabelsMutationVariables>({
                        mutation: DeleteLabelsDocument,
                        variables: {
                            id: label.id,
                        },
                    });

                    // show success
                    message.success({
                        content: t('labelDetails:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    // then go back to list
                    navigate('/admin/labels');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, label.id, navigate, t]);

    if (company && !company.availableModules.hasLabelsModules) {
        return (
            <ConsolePageWithHeader
                onBack={() => navigate('/admin/labels')}
                title={t('labelDetails:title', { name: initialValues.labelName.defaultValue })}
            >
                <NoItemResult subTitle={t('labelsList:moduleNotAvailable')} />;
            </ConsolePageWithHeader>
        );
    }

    const [hasUpdatePermission, hasDeletePermission] = useMemo(
        () => [
            hasPermissions(label.permissions, [permissionKind.updateLabel]),
            hasPermissions(label.permissions, [permissionKind.deleteLabel]),
        ],
        [label]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <ConsolePageWithHeader
                    extra={
                        hasDeletePermission && (
                            <Button onClick={onDeleteLabel} danger>
                                {t('labelDetails:actions.delete')}
                            </Button>
                        )
                    }
                    footer={[
                        hasUpdatePermission && (
                            <Button key="labelUpdate" form="labelUpdate" htmlType="submit" type="primary">
                                {t('labelDetails:actions.update')}
                            </Button>
                        ),
                    ]}
                    onBack={() => navigate('/admin/labels')}
                    title={
                        screens.md
                            ? t('labelDetails:title', { name: initialValues.labelName.defaultValue })
                            : initialValues.labelName.defaultValue
                    }
                >
                    <Form id="labelUpdate" name="labelUpdate" onSubmitCapture={handleSubmit}>
                        <LabelForm disabled={!hasUpdatePermission} label={label} offset={offset} updated={updated} />
                    </Form>
                </ConsolePageWithHeader>
            )}
        </Formik>
    );
};

export default LabelsDetailsInnerPage;
